// src/pages/RegistrationForm.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Accordion } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import 'react-phone-number-input/style.css';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { backend } from '../authConfig';
import { validateNif, validateIdNumber, validateEmail, validatePostalCode, formatIdNumber } from '../utils';
import './RegistrationForm.css';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // JWT token decoding function
  const decodeJWT = (token) => {
    try {
      console.log('🔍 Attempting to decode JWT token:', token);
      console.log('🔍 Token length:', token.length);
      
      // JWT has three parts separated by dots
      const parts = token.split('.');
      console.log('🔍 JWT parts count:', parts.length);
      console.log('🔍 JWT parts:', parts);
      
      if (parts.length !== 3) {
        console.error('❌ Invalid JWT token format - expected 3 parts, got', parts.length);
        return null;
      }
      
      // Decode the header (first part) for debugging
      const header = parts[0];
      console.log('🔍 Header part:', header);
      
      // Decode the payload (second part)
      const payload = parts[1];
      console.log('🔍 Payload part:', payload);
      console.log('🔍 Payload length:', payload.length);
      
      // Add padding if necessary for proper base64 decoding
      const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
      console.log('🔍 Padded payload:', paddedPayload);
      
      const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
      console.log('🔍 Decoded payload string:', decodedPayload);
      
      const parsedPayload = JSON.parse(decodedPayload);
      console.log('✅ Successfully parsed JWT payload:', parsedPayload);
      
      return parsedPayload;
    } catch (error) {
      console.error('❌ Error decoding JWT token:', error);
      console.error('❌ Error stack:', error.stack);
      return null;
    }
  };
  
  const [formData, setFormData] = useState({
    // Contact Info (maps to TENANT_Email in backend)
    TENANT_Email: '',
    
    // Section A - Rental Information
    CONTRACT_DateFrom: `${new Date().getFullYear()}-09-01`, // Dynamic default to September 1st of current year
    CONTRACT_PropertyId: '', // Backend expects just the property ID (P01, P02, etc.)
    CONTRACT_UnitId: '',
    
    // Tenant names and phone (moved to Section B)
    TENANT_FirstName: '',
    TENANT_LastName: '',
    TENANT_MobilePhoneNumber: '',
    
    // Section B - Tenant Details
    TENANT_NaturalidadeFreguesia: '',
    TENANT_NaturalidadeConcelho: '',
    TENANT_EstadoCivil: '',
    TENANT_MORADA_Rua: '',
    TENANT_MORADA_CodigoPostal: '',
    TENANT_MORADA_Localidade: '',
    TENANT_ID_CARD_Number: '',
    TENANT_ID_CARD_ExpirationDate: '',
    TENANT_NIF: '',
    
    // Section C - Guarantor 1
    FIADOR1_Relationship: '',
    FIADOR1_Name: '',
    FIADOR1_NaturalidadeFreguesia: '',
    FIADOR1_NaturalidadeConcelho: '',
    FIADOR1_ID_CARD_Number: '',
    FIADOR1_ID_CARD_ExpirationDate: '',
    FIADOR1_NIF: '',
    FIADOR1_EstadoCivil: '',
    FIADOR1_MORADA_Rua: '',
    FIADOR1_MORADA_CodigoPostal: '',
    FIADOR1_MORADA_Localidade: '',
    FIADOR1_MobilePhoneNumber: '',
    FIADOR1_Email: '',
    
    // Section D - Guarantor 2
    FIADOR2_Relationship: '',
    FIADOR2_Name: '',
    FIADOR2_NaturalidadeFreguesia: '',
    FIADOR2_NaturalidadeConcelho: '',
    FIADOR2_ID_CARD_Number: '',
    FIADOR2_ID_CARD_ExpirationDate: '',
    FIADOR2_NIF: '',
    FIADOR2_EstadoCivil: '',
    FIADOR2_MORADA_Rua: '',
    FIADOR2_MORADA_CodigoPostal: '',
    FIADOR2_MORADA_Localidade: '',
    FIADOR2_MobilePhoneNumber: '',
    FIADOR2_Email: ''
  });

  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('danger');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isUnitIdFromJWT, setIsUnitIdFromJWT] = useState(false);
  const [isEmailFromJWT, setIsEmailFromJWT] = useState(false);
  const [isNifFromJWT, setIsNifFromJWT] = useState(false);
  const [isPropertyIdFromJWT, setIsPropertyIdFromJWT] = useState(false);

  // JWT token processing on component mount
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('🚀 JWT useEffect triggered');
      console.log('🔍 All search params:', Object.fromEntries(searchParams.entries()));
    }
    
    const token = searchParams.get('token');
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 Token from searchParams:', token);
    }
    
    if (token) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ JWT token found in URL parameters');
      }
      const decodedToken = decodeJWT(token);
      
      if (decodedToken) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('✅ Successfully decoded JWT token:', decodedToken);
        }
        
        // Check if token has expired
        const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
        if (process.env.NODE_ENV !== 'production') {
          console.log('🕐 Current time (seconds):', currentTime);
          console.log('🕐 Token expiration (seconds):', decodedToken.exp);
          console.log('🕐 Time until expiration:', decodedToken.exp - currentTime, 'seconds');
        }
        
        if (decodedToken.exp && decodedToken.exp < currentTime) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('⚠️ JWT token has expired');
          }
          setIsTokenExpired(true);
          setAlertMessage('O link expirou. Por favor, solicite um novo convite.');
          setAlertType('warning');
          setShowAlert(true);
          return;
        }
        
        // Extract unit_id, email, nif, and property_id from the token and pre-fill the form
        const fieldsToUpdate = {};
        let hasDataToFill = false;
        
        if (decodedToken.property_id) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('✅ Pre-filling CONTRACT_PropertyId with:', decodedToken.property_id);
          }
          fieldsToUpdate.CONTRACT_PropertyId = decodedToken.property_id;
          setIsPropertyIdFromJWT(true); // Mark property_id as read-only
          hasDataToFill = true;
        }
        
        if (decodedToken.unit_id) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('✅ Pre-filling CONTRACT_UnitId with:', decodedToken.unit_id);
          }
          fieldsToUpdate.CONTRACT_UnitId = String(decodedToken.unit_id);
          setIsUnitIdFromJWT(true); // Mark unit_id as read-only
          hasDataToFill = true;
        }
        
        if (decodedToken.email) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('✅ Pre-filling TENANT_Email with:', decodedToken.email);
          }
          fieldsToUpdate.TENANT_Email = decodedToken.email;
          setIsEmailFromJWT(true); // Mark email as pre-filled
          hasDataToFill = true;
        }
        
        if (decodedToken.nif) {
          if (process.env.NODE_ENV !== 'production') {
            console.log('✅ Pre-filling TENANT_NIF with:', decodedToken.nif);
          }
          fieldsToUpdate.TENANT_NIF = decodedToken.nif;
          setIsNifFromJWT(true); // Mark NIF as pre-filled
          hasDataToFill = true;
        }
        
        if (hasDataToFill) {
          setFormData(prev => ({
            ...prev,
            ...fieldsToUpdate
          }));
          
          setAlertMessage('Formulário pré-preenchido com dados do convite.');
          setAlertType('success');
          setShowAlert(true);
        } else {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('⚠️ No data found in JWT token to pre-fill');
          }
          setAlertMessage('Token válido mas sem informações para pré-preenchimento.');
          setAlertType('warning');
          setShowAlert(true);
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.error('❌ Failed to decode JWT token');
        }
        setAlertMessage('Token inválido. Por favor, verifique o link.');
        setAlertType('danger');
        setShowAlert(true);
      }
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.log('ℹ️ No JWT token found in URL parameters');
      }
    }
  }, [searchParams]); // Run effect when searchParams change

  const handleInputChange = (field, value) => {
    // Don't format during typing - let users type freely
    // Formatting will happen on blur
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Individual field validation handlers for onBlur
  const validateField = (field, value) => {
    let error = '';
    
    try {
      switch (field) {
        case 'TENANT_Email':
          if (!value) error = 'Email é obrigatório';
          else if (!validateEmail(value)) error = 'Email inválido';
          break;
        case 'TENANT_NIF':
          if (!value) error = 'NIF é obrigatório';
          else if (!validateNif(value)) error = 'NIF inválido';
          break;
        case 'TENANT_ID_CARD_Number':
          if (!value) error = 'Número do cartão de cidadão é obrigatório';
          else if (!validateIdNumber(value)) error = 'Número do cartão de cidadão inválido';
          break;
        case 'TENANT_MobilePhoneNumber':
          if (!value) error = 'Telefone é obrigatório';
          else if (!isValidPhoneNumber(value)) error = 'Número de telefone inválido';
          break;
        case 'FIADOR1_NIF':
          if (!value) error = 'NIF do fiador 1 é obrigatório';
          else if (!validateNif(value)) error = 'NIF do fiador 1 inválido';
          break;
        case 'FIADOR1_ID_CARD_Number':
          if (!value) error = 'Número do cartão de cidadão do fiador 1 é obrigatório';
          else if (!validateIdNumber(value)) error = 'Número do cartão de cidadão do fiador 1 inválido';
          break;
        case 'FIADOR1_MobilePhoneNumber':
          if (!value) error = 'Telefone do fiador 1 é obrigatório';
          else if (!isValidPhoneNumber(value)) error = 'Telefone do fiador 1 inválido';
          break;
        case 'FIADOR2_NIF':
          if (!value) error = 'NIF do fiador 2 é obrigatório';
          else if (!validateNif(value)) error = 'NIF do fiador 2 inválido';
          break;
        case 'FIADOR2_ID_CARD_Number':
          if (!value) error = 'Número do cartão de cidadão do fiador 2 é obrigatório';
          else if (!validateIdNumber(value)) error = 'Número do cartão de cidadão do fiador 2 inválido';
          break;
        case 'FIADOR2_MobilePhoneNumber':
          if (!value) error = 'Telefone do fiador 2 é obrigatório';
          else if (!isValidPhoneNumber(value)) error = 'Telefone do fiador 2 inválido';
          break;
        case 'TENANT_MORADA_CodigoPostal':
          if (!value) error = 'Código postal é obrigatório';
          else if (!validatePostalCode(value)) error = 'Código postal inválido (formato: 1234-567)';
          break;
        case 'FIADOR1_MORADA_CodigoPostal':
          if (!value) error = 'Código postal do fiador 1 é obrigatório';
          else if (!validatePostalCode(value)) error = 'Código postal do fiador 1 inválido';
          break;
        case 'FIADOR2_MORADA_CodigoPostal':
          if (!value) error = 'Código postal do fiador 2 é obrigatório';
          else if (!validatePostalCode(value)) error = 'Código postal do fiador 2 inválido';
          break;
        case 'FIADOR1_Email':
          if (!value) error = 'Email do fiador 1 é obrigatório';
          else if (!validateEmail(value)) error = 'Email do fiador 1 inválido';
          break;
        case 'FIADOR2_Email':
          if (!value) error = 'Email do fiador 2 é obrigatório';
          else if (!validateEmail(value)) error = 'Email do fiador 2 inválido';
          break;
        default:
          break;
      }
    } catch (validationError) {
      error = 'Erro na validação';
      if (process.env.NODE_ENV !== 'production') {
        console.error('Validation error for field', field, ':', validationError);
      }
    }
    
    // Console log validation errors for debugging
    if (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`🔴 Validation Error - Field: ${field}, Value: "${value}", Error: ${error}`);
      }
      setErrors(prev => ({ ...prev, [field]: error }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFieldBlur = (field, value) => {
    // Format ID card numbers when user finishes typing
    if (field.includes('ID_CARD_Number') && value) {
      const formattedValue = formatIdNumber(value);
      if (formattedValue !== value) {
        setFormData(prev => ({ ...prev, [field]: formattedValue }));
        // Validate the formatted value
        validateField(field, formattedValue);
        return;
      }
    }
    
    validateField(field, value);
  };

  const validateForm = () => {
    const newErrors = {};
    
    try {
      // Contact validation
      if (!formData.TENANT_Email) newErrors.TENANT_Email = 'Email é obrigatório';
      else if (!validateEmail(formData.TENANT_Email)) newErrors.TENANT_Email = 'Email inválido';
      
      // Section A validations - All mandatory
      if (!formData.CONTRACT_DateFrom) newErrors.CONTRACT_DateFrom = 'Data de início é obrigatória';
      if (!formData.CONTRACT_PropertyId) newErrors.CONTRACT_PropertyId = 'Unidade de arrendamento é obrigatória';
      if (!formData.CONTRACT_UnitId) newErrors.CONTRACT_UnitId = 'Número da unidade é obrigatório';
      if (!formData.TENANT_FirstName) newErrors.TENANT_FirstName = 'Nome próprio é obrigatório';
      if (!formData.TENANT_LastName) newErrors.TENANT_LastName = 'Nome de família é obrigatório';
      if (!formData.TENANT_MobilePhoneNumber) newErrors.TENANT_MobilePhoneNumber = 'Telefone é obrigatório';
      else if (!isValidPhoneNumber(formData.TENANT_MobilePhoneNumber)) newErrors.TENANT_MobilePhoneNumber = 'Número de telefone inválido';
      
      // Section B validations - All mandatory
      if (!formData.TENANT_NaturalidadeFreguesia) newErrors.TENANT_NaturalidadeFreguesia = 'Freguesia de nascimento é obrigatória';
      if (!formData.TENANT_NaturalidadeConcelho) newErrors.TENANT_NaturalidadeConcelho = 'Concelho de nascimento é obrigatório';
      if (!formData.TENANT_EstadoCivil) newErrors.TENANT_EstadoCivil = 'Estado civil é obrigatório';
      if (!formData.TENANT_MORADA_Rua) newErrors.TENANT_MORADA_Rua = 'Morada é obrigatória';
      if (!formData.TENANT_MORADA_CodigoPostal) newErrors.TENANT_MORADA_CodigoPostal = 'Código postal é obrigatório';
      else if (!validatePostalCode(formData.TENANT_MORADA_CodigoPostal)) newErrors.TENANT_MORADA_CodigoPostal = 'Código postal inválido (formato: 1234-567)';
      if (!formData.TENANT_MORADA_Localidade) newErrors.TENANT_MORADA_Localidade = 'Localidade é obrigatória';
      if (!formData.TENANT_ID_CARD_Number) newErrors.TENANT_ID_CARD_Number = 'Número do cartão de cidadão é obrigatório';
      else if (!validateIdNumber(formData.TENANT_ID_CARD_Number)) newErrors.TENANT_ID_CARD_Number = 'Número do cartão de cidadão inválido';
      if (!formData.TENANT_ID_CARD_ExpirationDate) newErrors.TENANT_ID_CARD_ExpirationDate = 'Data de validade do cartão é obrigatória';
      if (!formData.TENANT_NIF) newErrors.TENANT_NIF = 'NIF é obrigatório';
      else if (!validateNif(formData.TENANT_NIF)) newErrors.TENANT_NIF = 'NIF inválido';
      
      // Guarantor 1 validations - All mandatory
      if (!formData.FIADOR1_Name) newErrors.FIADOR1_Name = 'Nome do fiador 1 é obrigatório';
      if (!formData.FIADOR1_Relationship) newErrors.FIADOR1_Relationship = 'Parentesco é obrigatório';
      if (!formData.FIADOR1_NaturalidadeFreguesia) newErrors.FIADOR1_NaturalidadeFreguesia = 'Freguesia de nascimento do fiador 1 é obrigatória';
      if (!formData.FIADOR1_NaturalidadeConcelho) newErrors.FIADOR1_NaturalidadeConcelho = 'Concelho de nascimento do fiador 1 é obrigatório';
      if (!formData.FIADOR1_EstadoCivil) newErrors.FIADOR1_EstadoCivil = 'Estado civil do fiador 1 é obrigatório';
      if (!formData.FIADOR1_ID_CARD_Number) newErrors.FIADOR1_ID_CARD_Number = 'Número do cartão de cidadão do fiador 1 é obrigatório';
      else if (!validateIdNumber(formData.FIADOR1_ID_CARD_Number)) newErrors.FIADOR1_ID_CARD_Number = 'Número do cartão de cidadão do fiador 1 inválido';
      if (!formData.FIADOR1_ID_CARD_ExpirationDate) newErrors.FIADOR1_ID_CARD_ExpirationDate = 'Data de validade do cartão do fiador 1 é obrigatória';
      if (!formData.FIADOR1_NIF) newErrors.FIADOR1_NIF = 'NIF do fiador 1 é obrigatório';
      else if (!validateNif(formData.FIADOR1_NIF)) newErrors.FIADOR1_NIF = 'NIF do fiador 1 inválido';
      if (!formData.FIADOR1_MORADA_Rua) newErrors.FIADOR1_MORADA_Rua = 'Morada do fiador 1 é obrigatória';
      if (!formData.FIADOR1_MORADA_CodigoPostal) newErrors.FIADOR1_MORADA_CodigoPostal = 'Código postal do fiador 1 é obrigatório';
      else if (!validatePostalCode(formData.FIADOR1_MORADA_CodigoPostal)) newErrors.FIADOR1_MORADA_CodigoPostal = 'Código postal do fiador 1 inválido';
      if (!formData.FIADOR1_MORADA_Localidade) newErrors.FIADOR1_MORADA_Localidade = 'Localidade do fiador 1 é obrigatória';
      if (!formData.FIADOR1_MobilePhoneNumber) newErrors.FIADOR1_MobilePhoneNumber = 'Telefone do fiador 1 é obrigatório';
      else if (!isValidPhoneNumber(formData.FIADOR1_MobilePhoneNumber)) newErrors.FIADOR1_MobilePhoneNumber = 'Telefone do fiador 1 inválido';
      if (!formData.FIADOR1_Email) newErrors.FIADOR1_Email = 'Email do fiador 1 é obrigatório';
      else if (!validateEmail(formData.FIADOR1_Email)) newErrors.FIADOR1_Email = 'Email do fiador 1 inválido';
      
      // Guarantor 2 validations - All mandatory
      if (!formData.FIADOR2_Name) newErrors.FIADOR2_Name = 'Nome do fiador 2 é obrigatório';
      if (!formData.FIADOR2_Relationship) newErrors.FIADOR2_Relationship = 'Parentesco é obrigatório';
      if (!formData.FIADOR2_NaturalidadeFreguesia) newErrors.FIADOR2_NaturalidadeFreguesia = 'Freguesia de nascimento do fiador 2 é obrigatória';
      if (!formData.FIADOR2_NaturalidadeConcelho) newErrors.FIADOR2_NaturalidadeConcelho = 'Concelho de nascimento do fiador 2 é obrigatório';
      if (!formData.FIADOR2_EstadoCivil) newErrors.FIADOR2_EstadoCivil = 'Estado civil do fiador 2 é obrigatório';
      if (!formData.FIADOR2_ID_CARD_Number) newErrors.FIADOR2_ID_CARD_Number = 'Número do cartão de cidadão do fiador 2 é obrigatório';
      else if (!validateIdNumber(formData.FIADOR2_ID_CARD_Number)) newErrors.FIADOR2_ID_CARD_Number = 'Número do cartão de cidadão do fiador 2 inválido';
      if (!formData.FIADOR2_ID_CARD_ExpirationDate) newErrors.FIADOR2_ID_CARD_ExpirationDate = 'Data de validade do cartão do fiador 2 é obrigatória';
      if (!formData.FIADOR2_NIF) newErrors.FIADOR2_NIF = 'NIF do fiador 2 é obrigatório';
      else if (!validateNif(formData.FIADOR2_NIF)) newErrors.FIADOR2_NIF = 'NIF do fiador 2 inválido';
      if (!formData.FIADOR2_MORADA_Rua) newErrors.FIADOR2_MORADA_Rua = 'Morada do fiador 2 é obrigatória';
      if (!formData.FIADOR2_MORADA_CodigoPostal) newErrors.FIADOR2_MORADA_CodigoPostal = 'Código postal do fiador 2 é obrigatório';
      else if (!validatePostalCode(formData.FIADOR2_MORADA_CodigoPostal)) newErrors.FIADOR2_MORADA_CodigoPostal = 'Código postal do fiador 2 inválido';
      if (!formData.FIADOR2_MORADA_Localidade) newErrors.FIADOR2_MORADA_Localidade = 'Localidade do fiador 2 é obrigatória';
      if (!formData.FIADOR2_MobilePhoneNumber) newErrors.FIADOR2_MobilePhoneNumber = 'Telefone do fiador 2 é obrigatório';
      else if (!isValidPhoneNumber(formData.FIADOR2_MobilePhoneNumber)) newErrors.FIADOR2_MobilePhoneNumber = 'Telefone do fiador 2 inválido';
      if (!formData.FIADOR2_Email) newErrors.FIADOR2_Email = 'Email do fiador 2 é obrigatório';
      else if (!validateEmail(formData.FIADOR2_Email)) newErrors.FIADOR2_Email = 'Email do fiador 2 inválido';
    } catch (validationError) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error during form validation:', validationError);
      }
      newErrors.general = 'Erro na validação do formulário';
    }
    
    setErrors(newErrors);
    
    // Console log validation summary for debugging
    const errorCount = Object.keys(newErrors).length;
    if (errorCount > 0) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(`🔴 Form Validation Failed - ${errorCount} errors found:`);
        Object.entries(newErrors).forEach(([field, error]) => {
          console.log(`  - ${field}: ${error}`);
        });
      }
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ Form Validation Passed - No errors found');
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setAlertMessage('Por favor, corrija os erros no formulário');
      setAlertType('danger');
      setShowAlert(true);
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const errorElement = document.getElementById(firstErrorField) || document.querySelector(`[name="${firstErrorField}"]`) || document.querySelector('.is-invalid');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Submitting form data:', formData);
      }
      
      // Call the Azure Function API endpoint using backend configuration
      const response = await fetch(backend.url_submit_form_contract, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      // Only throw for non-400 errors (400 may contain validation errors we want to process)
      if (!response.ok && response.status !== 400) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('Contract created successfully:', result);
        }
        setAlertMessage(`Registo submetido com sucesso! ID do contrato: ${result.contract_id}`);
        setAlertType('success');
        setShowAlert(true);
        
        // Reset form after successful submission
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.error('API error:', result);
        }
        
        // Handle validation errors from the server
        if (result.validation_errors && Object.keys(result.validation_errors).length > 0) {
          const translatedErrors = {};
          let errorMessages = [];
          
          Object.entries(result.validation_errors).forEach(([field, error]) => {
            // Show server validation error for any field
            translatedErrors[field] = error || 'Erro de validação do servidor';
            errorMessages.push(`${getFieldDisplayName(field)}: ${error || 'Erro de validação'}`);
          });
          
          // Update form errors with server validation errors
          setErrors(prev => ({ ...prev, ...translatedErrors }));
          
          // Show alert with server error details
          setAlertMessage(`Erros encontrados:\n${errorMessages.join('\n')}`);
          setAlertType('danger');
          setShowAlert(true);
          
          // Scroll to first server error
          const firstServerErrorField = Object.keys(translatedErrors)[0];
          if (firstServerErrorField) {
            const errorElement = document.getElementById(firstServerErrorField) || document.querySelector(`[name="${firstServerErrorField}"]`);
            if (errorElement) {
              setTimeout(() => {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                errorElement.focus();
              }, 100);
            }
          }
        } else {
          // Generic server error
          setAlertMessage(result.error || 'Erro ao submeter o registo. Tente novamente.');
          setAlertType('danger');
          setShowAlert(true);
        }
      }
      
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Network error submitting form:', error);
      }
      setAlertMessage('Erro de ligação ao servidor. Verifique a sua ligação à internet e tente novamente.');
      setAlertType('danger');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to get user-friendly field names for error messages
  const getFieldDisplayName = (field) => {
    const fieldNames = {
      'TENANT_Email': 'Email do inquilino',
      'FIADOR1_Email': 'Email do fiador 1',
      'FIADOR2_Email': 'Email do fiador 2',
      'TENANT_ID_CARD_Number': 'Cartão de Cidadão do inquilino',
      'FIADOR1_ID_CARD_Number': 'Cartão de Cidadão do fiador 1',
      'FIADOR2_ID_CARD_Number': 'Cartão de Cidadão do fiador 2',
      'TENANT_NIF': 'NIF do inquilino',
      'FIADOR1_NIF': 'NIF do fiador 1',
      'FIADOR2_NIF': 'NIF do fiador 2',
      'CONTRACT_PropertyId': 'Imóvel',
      'CONTRACT_UnitId': 'Unidade'
    };
    
    return fieldNames[field] || field;
  };

  // Development helper function for form prefilling
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.prefillFormForTesting = () => {
        const sampleData = {
          "TENANT_Email": "me@airesmarques.ch",
          "CONTRACT_DateFrom": "2025-09-01",
          "CONTRACT_PropertyId": "P01",
          "CONTRACT_UnitId": "02",
          "TENANT_FirstName": "Aires Manuel",
          "TENANT_LastName": "Carreira e Marques",
          "TENANT_MobilePhoneNumber": "+351966111111",
          "TENANT_NaturalidadeFreguesia": "Porto",
          "TENANT_NaturalidadeConcelho": "Santo Ildefonso",
          "TENANT_EstadoCivil": "casado",
          "TENANT_MORADA_Rua": "Hof 265",
          "TENANT_MORADA_CodigoPostal": "3000-111",
          "TENANT_MORADA_Localidade": "Guia",
          "TENANT_ID_CARD_Number": "11085142 0 ZX5",
          "TENANT_ID_CARD_ExpirationDate": "2025-09-26",
          "TENANT_NIF": "215656393",
          "FIADOR1_Relationship": "Pai",
          "FIADOR1_Name": "Diamantino Monteiro Marques",
          "FIADOR1_NaturalidadeFreguesia": "Albergaria dos 12",
          "FIADOR1_NaturalidadeConcelho": "Pombal",
          "FIADOR1_EstadoCivil": "casado",
          "FIADOR1_ID_CARD_Number": "33076639 2 ZW6",
          "FIADOR1_ID_CARD_ExpirationDate": "2025-07-30",
          "FIADOR1_NIF": "250891972",
          "FIADOR1_MORADA_Rua": "Guia 222",
          "FIADOR1_MORADA_CodigoPostal": "2222-222",
          "FIADOR1_MORADA_Localidade": "Guia2",
          "FIADOR1_MobilePhoneNumber": "+351966222222",
          "FIADOR1_Email": "diamar33@gmail.com",
          "FIADOR2_Relationship": "Mãe",
          "FIADOR2_Name": "Maria 3",
          "FIADOR2_NaturalidadeFreguesia": "Freguesia 3",
          "FIADOR2_NaturalidadeConcelho": "Pombal 3",
          "FIADOR2_EstadoCivil": "casado",
          "FIADOR2_ID_CARD_Number": "12076313 3 ZZ6",
          "FIADOR2_ID_CARD_ExpirationDate": "2025-11-03",
          "FIADOR2_NIF": "287175073",
          "FIADOR2_MORADA_Rua": "Rua 3 da Guia",
          "FIADOR2_MORADA_CodigoPostal": "3333-333",
          "FIADOR2_MORADA_Localidade": "Localidade 3",
          "FIADOR2_MobilePhoneNumber": "+351966333333",
          "FIADOR2_Email": "gracinela@gmail.com"
        };

        console.log('🚀 Prefilling form with sample data...');
        setFormData(prev => ({ ...prev, ...sampleData }));
        console.log('✅ Form prefilled successfully!');
      };
    }
  }, []);

  const maritalStatusOptions = [
    { value: '', label: 'Selecione...' },
    { value: 'solteiro', label: 'Solteiro(a)' },
    { value: 'casado', label: 'Casado(a)' },
    { value: 'divorciado', label: 'Divorciado(a)' },
    { value: 'viuvo', label: 'Viúvo(a)' },
    { value: 'uniao_facto', label: 'União de Facto' }
  ];

  const relationshipOptions = [
    { value: '', label: 'Selecione...' },
    { value: 'Pai', label: 'Pai' },
    { value: 'Mãe', label: 'Mãe' }
  ];

  // Property mapping - maps property IDs to their display names
  const propertyMapping = {
    'P01': 'P01 - Rua Travessa Coimbra'
  };

  // Generate property options based on JWT pre-filling
  const getPropertyOptions = () => {
    if (isPropertyIdFromJWT && formData.CONTRACT_PropertyId) {
      // If property is from JWT, only show that property
      const propertyId = formData.CONTRACT_PropertyId;
      const displayName = propertyMapping[propertyId] || `${propertyId} - Imóvel`;
      return [
        { value: propertyId, label: displayName }
      ];
    } else {
      // If no JWT property, show default selection option
      return [
        { value: '', label: 'Selecione um imóvel...' }
      ];
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          <Card className="registration-form">
            <Card.Header>
              <h2 className="text-center mb-0">Formulário de Registo de Arrendamento</h2>
            </Card.Header>
            <Card.Body>
              {showAlert && (
                <Alert variant={alertType} onClose={() => setShowAlert(false)} dismissible>
                  {alertMessage}
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit} noValidate>
                <Accordion defaultActiveKey="0">
                  {/* Contact Information */}
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Contacto</Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                              type="email"
                              value={formData.TENANT_Email}
                              onChange={(e) => handleInputChange('TENANT_Email', e.target.value)}
                              onBlur={(e) => handleFieldBlur('TENANT_Email', e.target.value)}
                              isInvalid={!!errors.TENANT_Email}
                              required
                              className={isEmailFromJWT ? 'border-success' : ''}
                              data-testid="tenant-email"
                            />
                            {isEmailFromJWT && (
                              <Form.Text className="text-success">
                                ✅ Pré-preenchido pelo convite
                              </Form.Text>
                            )}
                            <Form.Control.Feedback type="invalid">{errors.TENANT_Email}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Section A - Rental Information */}
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>A - Arrendamento</Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Data de Início de Arrendamento *</Form.Label>
                            <Form.Control
                              type="date"
                              value={formData.CONTRACT_DateFrom}
                              onChange={(e) => handleInputChange('CONTRACT_DateFrom', e.target.value)}
                              isInvalid={!!errors.CONTRACT_DateFrom}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.CONTRACT_DateFrom}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Unidade de Arrendamento *</Form.Label>
                            <Form.Control
                              as="select"
                              value={formData.CONTRACT_PropertyId}
                              onChange={(e) => handleInputChange('CONTRACT_PropertyId', e.target.value)}
                              isInvalid={!!errors.CONTRACT_PropertyId}
                              required
                              readOnly={isPropertyIdFromJWT}
                              disabled={isPropertyIdFromJWT}
                              className={isPropertyIdFromJWT ? 'bg-light' : ''}
                              id="CONTRACT_PropertyId"
                              name="CONTRACT_PropertyId"
                              aria-label="Property selection"
                              data-testid="contract-property-id"
                            >
                              {getPropertyOptions().map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Form.Control>
                            {isPropertyIdFromJWT && (
                              <Form.Text className="text-muted">
                                ✅ Pré-preenchido pelo convite (não editável)
                              </Form.Text>
                            )}
                            <Form.Control.Feedback type="invalid">{errors.CONTRACT_PropertyId}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Número Unidade/Quarto *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.CONTRACT_UnitId}
                              onChange={(e) => handleInputChange('CONTRACT_UnitId', e.target.value)}
                              isInvalid={!!errors.CONTRACT_UnitId}
                              required
                              readOnly={isUnitIdFromJWT}
                              disabled={isUnitIdFromJWT}
                              className={isUnitIdFromJWT ? 'bg-light' : ''}
                              id="CONTRACT_UnitId"
                              name="CONTRACT_UnitId"
                              aria-label="Unit number"
                              data-testid="contract-unit-id"
                            />
                            {isUnitIdFromJWT && (
                              <Form.Text className="text-muted">
                                ✅ Pré-preenchido pelo convite (não editável)
                              </Form.Text>
                            )}
                            <Form.Control.Feedback type="invalid">{errors.CONTRACT_UnitId}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Section B - Tenant Details */}
                  <Accordion.Item eventKey="2">
                    <Accordion.Header>B - Detalhes do Inquilino</Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Inquilino - Nomes Próprios *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_FirstName}
                              onChange={(e) => handleInputChange('TENANT_FirstName', e.target.value)}
                              isInvalid={!!errors.TENANT_FirstName}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_FirstName}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Inquilino - Nomes de Família *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_LastName}
                              onChange={(e) => handleInputChange('TENANT_LastName', e.target.value)}
                              isInvalid={!!errors.TENANT_LastName}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_LastName}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={12}>
                          <Form.Group className="mb-3">
                            <Form.Label>Inquilino - Número Telemóvel *</Form.Label>
                            <PhoneInput
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="PT"
                              value={formData.TENANT_MobilePhoneNumber}
                              onChange={(value) => handleInputChange('TENANT_MobilePhoneNumber', value)}
                              onBlur={() => validateField('TENANT_MobilePhoneNumber', formData.TENANT_MobilePhoneNumber)}
                              className={`form-control ${errors.TENANT_MobilePhoneNumber ? 'is-invalid' : ''}`}
                              name="TENANT_MobilePhoneNumber"
                              id="TENANT_MobilePhoneNumber"
                              aria-label="Mobile phone number"
                            />
                            {errors.TENANT_MobilePhoneNumber && <div className="invalid-feedback d-block">{errors.TENANT_MobilePhoneNumber}</div>}
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Natural da Freguesia *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_NaturalidadeFreguesia}
                              onChange={(e) => handleInputChange('TENANT_NaturalidadeFreguesia', e.target.value)}
                              isInvalid={!!errors.TENANT_NaturalidadeFreguesia}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_NaturalidadeFreguesia}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Natural do Concelho *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_NaturalidadeConcelho}
                              onChange={(e) => handleInputChange('TENANT_NaturalidadeConcelho', e.target.value)}
                              isInvalid={!!errors.TENANT_NaturalidadeConcelho}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_NaturalidadeConcelho}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Estado Civil *</Form.Label>
                            <Form.Select
                              value={formData.TENANT_EstadoCivil}
                              onChange={(e) => handleInputChange('TENANT_EstadoCivil', e.target.value)}
                              isInvalid={!!errors.TENANT_EstadoCivil}
                              required
                            >
                              {maritalStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.TENANT_EstadoCivil}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Morada - Rua, número, andar *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_MORADA_Rua}
                              onChange={(e) => handleInputChange('TENANT_MORADA_Rua', e.target.value)}
                              isInvalid={!!errors.TENANT_MORADA_Rua}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_MORADA_Rua}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Código Postal *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_MORADA_CodigoPostal}
                              onChange={(e) => handleInputChange('TENANT_MORADA_CodigoPostal', e.target.value)}
                              onBlur={(e) => handleFieldBlur('TENANT_MORADA_CodigoPostal', e.target.value)}
                              isInvalid={!!errors.TENANT_MORADA_CodigoPostal}
                              placeholder="1234-567"
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_MORADA_CodigoPostal}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Localidade *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_MORADA_Localidade}
                              onChange={(e) => handleInputChange('TENANT_MORADA_Localidade', e.target.value)}
                              isInvalid={!!errors.TENANT_MORADA_Localidade}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_MORADA_Localidade}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Número Cartão Cidadão *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_ID_CARD_Number}
                              onChange={(e) => handleInputChange('TENANT_ID_CARD_Number', e.target.value)}
                              onBlur={(e) => handleFieldBlur('TENANT_ID_CARD_Number', e.target.value)}
                              isInvalid={!!errors.TENANT_ID_CARD_Number}
                              placeholder="09999999 9 ZZ4"
                              maxLength={20}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_ID_CARD_Number}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Data Validade Cartão Cidadão *</Form.Label>
                            <Form.Control
                              type="date"
                              value={formData.TENANT_ID_CARD_ExpirationDate}
                              onChange={(e) => handleInputChange('TENANT_ID_CARD_ExpirationDate', e.target.value)}
                              isInvalid={!!errors.TENANT_ID_CARD_ExpirationDate}
                              required
                            />
                            <Form.Control.Feedback type="invalid">{errors.TENANT_ID_CARD_ExpirationDate}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>NIF *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.TENANT_NIF}
                              onChange={(e) => handleInputChange('TENANT_NIF', e.target.value)}
                              onBlur={(e) => handleFieldBlur('TENANT_NIF', e.target.value)}
                              isInvalid={!!errors.TENANT_NIF}
                              placeholder="123456789"
                              maxLength={9}
                              required
                              className={isNifFromJWT ? 'border-success' : ''}
                              id="TENANT_NIF"
                              name="TENANT_NIF"
                              aria-label="Tax ID number"
                            />
                            {isNifFromJWT && (
                              <Form.Text className="text-success">
                                ✅ Pré-preenchido pelo convite
                              </Form.Text>
                            )}
                            <Form.Control.Feedback type="invalid">{errors.TENANT_NIF}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Section C - Guarantor 1 */}
                  <Accordion.Item eventKey="3">
                    <Accordion.Header>C - Fiador 1</Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Parentesco *</Form.Label>
                            <Form.Select
                              value={formData.FIADOR1_Relationship}
                              onChange={(e) => handleInputChange('FIADOR1_Relationship', e.target.value)}
                              isInvalid={!!errors.FIADOR1_Relationship}
                              required
                              id="FIADOR1_Relationship"
                              name="FIADOR1_Relationship"
                              aria-label="Select relationship"
                            >
                              {relationshipOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_Relationship}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Nome Completo *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_Name}
                              onChange={(e) => handleInputChange('FIADOR1_Name', e.target.value)}
                              isInvalid={!!errors.FIADOR1_Name}
                              required
                              id="FIADOR1_Name"
                              name="FIADOR1_Name"
                              aria-label="Full name"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_Name}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Naturalidade - Freguesia *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_NaturalidadeFreguesia}
                              onChange={(e) => handleInputChange('FIADOR1_NaturalidadeFreguesia', e.target.value)}
                              isInvalid={!!errors.FIADOR1_NaturalidadeFreguesia}
                              required
                              id="FIADOR1_NaturalidadeFreguesia"
                              name="FIADOR1_NaturalidadeFreguesia"
                              aria-label="Birth parish"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_NaturalidadeFreguesia}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Naturalidade - Concelho *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_NaturalidadeConcelho}
                              onChange={(e) => handleInputChange('FIADOR1_NaturalidadeConcelho', e.target.value)}
                              isInvalid={!!errors.FIADOR1_NaturalidadeConcelho}
                              required
                              id="FIADOR1_NaturalidadeConcelho"
                              name="FIADOR1_NaturalidadeConcelho"
                              aria-label="Birth municipality"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_NaturalidadeConcelho}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Cartão Cidadão - Número *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_ID_CARD_Number}
                              onChange={(e) => handleInputChange('FIADOR1_ID_CARD_Number', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR1_ID_CARD_Number', e.target.value)}
                              isInvalid={!!errors.FIADOR1_ID_CARD_Number}
                              placeholder="09999999 9 ZZ4"
                              maxLength={20}
                              required
                              id="FIADOR1_ID_CARD_Number"
                              name="FIADOR1_ID_CARD_Number"
                              aria-label="ID card number"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_ID_CARD_Number}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Cartão Cidadão - Data Validade *</Form.Label>
                            <Form.Control
                              type="date"
                              value={formData.FIADOR1_ID_CARD_ExpirationDate}
                              onChange={(e) => handleInputChange('FIADOR1_ID_CARD_ExpirationDate', e.target.value)}
                              isInvalid={!!errors.FIADOR1_ID_CARD_ExpirationDate}
                              required
                              id="FIADOR1_ID_CARD_ExpirationDate"
                              name="FIADOR1_ID_CARD_ExpirationDate"
                              aria-label="ID card expiration date"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_ID_CARD_ExpirationDate}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>NIF *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_NIF}
                              onChange={(e) => handleInputChange('FIADOR1_NIF', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR1_NIF', e.target.value)}
                              isInvalid={!!errors.FIADOR1_NIF}
                              maxLength={9}
                              required
                              id="FIADOR1_NIF"
                              name="FIADOR1_NIF"
                              aria-label="Tax ID number"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_NIF}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Estado Civil *</Form.Label>
                            <Form.Select
                              value={formData.FIADOR1_EstadoCivil}
                              onChange={(e) => handleInputChange('FIADOR1_EstadoCivil', e.target.value)}
                              isInvalid={!!errors.FIADOR1_EstadoCivil}
                              required
                              id="FIADOR1_EstadoCivil"
                              name="FIADOR1_EstadoCivil"
                              aria-label="Select marital status"
                            >
                              {maritalStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_EstadoCivil}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Morada - Rua, número, andar *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_MORADA_Rua}
                              onChange={(e) => handleInputChange('FIADOR1_MORADA_Rua', e.target.value)}
                              isInvalid={!!errors.FIADOR1_MORADA_Rua}
                              required
                              id="FIADOR1_MORADA_Rua"
                              name="FIADOR1_MORADA_Rua"
                              aria-label="Street address"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_MORADA_Rua}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Código Postal *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_MORADA_CodigoPostal}
                              onChange={(e) => handleInputChange('FIADOR1_MORADA_CodigoPostal', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR1_MORADA_CodigoPostal', e.target.value)}
                              isInvalid={!!errors.FIADOR1_MORADA_CodigoPostal}
                              placeholder="1234-567"
                              required
                              id="FIADOR1_MORADA_CodigoPostal"
                              name="FIADOR1_MORADA_CodigoPostal"
                              aria-label="Postal code"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_MORADA_CodigoPostal}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Localidade *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR1_MORADA_Localidade}
                              onChange={(e) => handleInputChange('FIADOR1_MORADA_Localidade', e.target.value)}
                              isInvalid={!!errors.FIADOR1_MORADA_Localidade}
                              required
                              id="FIADOR1_MORADA_Localidade"
                              name="FIADOR1_MORADA_Localidade"
                              aria-label="Locality"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_MORADA_Localidade}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Número de Telefone *</Form.Label>
                            <PhoneInput
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="PT"
                              value={formData.FIADOR1_MobilePhoneNumber}
                              onChange={(value) => handleInputChange('FIADOR1_MobilePhoneNumber', value)}
                              onBlur={() => validateField('FIADOR1_MobilePhoneNumber', formData.FIADOR1_MobilePhoneNumber)}
                              className={`form-control ${errors.FIADOR1_MobilePhoneNumber ? 'is-invalid' : ''}`}
                              name="FIADOR1_MobilePhoneNumber"
                              id="FIADOR1_MobilePhoneNumber"
                              aria-label="Phone number"
                            />
                            {errors.FIADOR1_MobilePhoneNumber && <div className="invalid-feedback d-block">{errors.FIADOR1_MobilePhoneNumber}</div>}
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                              type="email"
                              value={formData.FIADOR1_Email}
                              onChange={(e) => handleInputChange('FIADOR1_Email', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR1_Email', e.target.value)}
                              isInvalid={!!errors.FIADOR1_Email}
                              required
                              id="FIADOR1_Email"
                              name="FIADOR1_Email"
                              aria-label="Email address"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR1_Email}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>

                  {/* Section D - Guarantor 2 */}
                  <Accordion.Item eventKey="4">
                    <Accordion.Header>D - Fiador 2</Accordion.Header>
                    <Accordion.Body>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Parentesco *</Form.Label>
                            <Form.Select
                              value={formData.FIADOR2_Relationship}
                              onChange={(e) => handleInputChange('FIADOR2_Relationship', e.target.value)}
                              isInvalid={!!errors.FIADOR2_Relationship}
                              required
                              id="FIADOR2_Relationship"
                              name="FIADOR2_Relationship"
                              aria-label="Select relationship"
                            >
                              {relationshipOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_Relationship}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Nome Completo *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_Name}
                              onChange={(e) => handleInputChange('FIADOR2_Name', e.target.value)}
                              isInvalid={!!errors.FIADOR2_Name}
                              required
                              id="FIADOR2_Name"
                              name="FIADOR2_Name"
                              aria-label="Full name"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_Name}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Naturalidade - Freguesia *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_NaturalidadeFreguesia}
                              onChange={(e) => handleInputChange('FIADOR2_NaturalidadeFreguesia', e.target.value)}
                              isInvalid={!!errors.FIADOR2_NaturalidadeFreguesia}
                              required
                              id="FIADOR2_NaturalidadeFreguesia"
                              name="FIADOR2_NaturalidadeFreguesia"
                              aria-label="Birth parish"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_NaturalidadeFreguesia}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Naturalidade - Concelho *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_NaturalidadeConcelho}
                              onChange={(e) => handleInputChange('FIADOR2_NaturalidadeConcelho', e.target.value)}
                              isInvalid={!!errors.FIADOR2_NaturalidadeConcelho}
                              required
                              id="FIADOR2_NaturalidadeConcelho"
                              name="FIADOR2_NaturalidadeConcelho"
                              aria-label="Birth municipality"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_NaturalidadeConcelho}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Cartão Cidadão - Número *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_ID_CARD_Number}
                              onChange={(e) => handleInputChange('FIADOR2_ID_CARD_Number', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR2_ID_CARD_Number', e.target.value)}
                              isInvalid={!!errors.FIADOR2_ID_CARD_Number}
                              placeholder="09999999 9 ZZ4"
                              maxLength={20}
                              required
                              id="FIADOR2_ID_CARD_Number"
                              name="FIADOR2_ID_CARD_Number"
                              aria-label="ID card number"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_ID_CARD_Number}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Cartão Cidadão - Data Validade *</Form.Label>
                            <Form.Control
                              type="date"
                              value={formData.FIADOR2_ID_CARD_ExpirationDate}
                              onChange={(e) => handleInputChange('FIADOR2_ID_CARD_ExpirationDate', e.target.value)}
                              isInvalid={!!errors.FIADOR2_ID_CARD_ExpirationDate}
                              required
                              id="FIADOR2_ID_CARD_ExpirationDate"
                              name="FIADOR2_ID_CARD_ExpirationDate"
                              aria-label="ID card expiration date"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_ID_CARD_ExpirationDate}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>NIF *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_NIF}
                              onChange={(e) => handleInputChange('FIADOR2_NIF', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR2_NIF', e.target.value)}
                              isInvalid={!!errors.FIADOR2_NIF}
                              maxLength={9}
                              required
                              id="FIADOR2_NIF"
                              name="FIADOR2_NIF"
                              aria-label="Tax ID number"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_NIF}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Estado Civil *</Form.Label>
                            <Form.Select
                              value={formData.FIADOR2_EstadoCivil}
                              onChange={(e) => handleInputChange('FIADOR2_EstadoCivil', e.target.value)}
                              isInvalid={!!errors.FIADOR2_EstadoCivil}
                              required
                              id="FIADOR2_EstadoCivil"
                              name="FIADOR2_EstadoCivil"
                              aria-label="Select marital status"
                            >
                              {maritalStatusOptions.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_EstadoCivil}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Morada - Rua, número, andar *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_MORADA_Rua}
                              onChange={(e) => handleInputChange('FIADOR2_MORADA_Rua', e.target.value)}
                              isInvalid={!!errors.FIADOR2_MORADA_Rua}
                              required
                              id="FIADOR2_MORADA_Rua"
                              name="FIADOR2_MORADA_Rua"
                              aria-label="Street address"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_MORADA_Rua}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Código Postal *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_MORADA_CodigoPostal}
                              onChange={(e) => handleInputChange('FIADOR2_MORADA_CodigoPostal', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR2_MORADA_CodigoPostal', e.target.value)}
                              isInvalid={!!errors.FIADOR2_MORADA_CodigoPostal}
                              placeholder="1234-567"
                              required
                              id="FIADOR2_MORADA_CodigoPostal"
                              name="FIADOR2_MORADA_CodigoPostal"
                              aria-label="Postal code"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_MORADA_CodigoPostal}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Localidade *</Form.Label>
                            <Form.Control
                              type="text"
                              value={formData.FIADOR2_MORADA_Localidade}
                              onChange={(e) => handleInputChange('FIADOR2_MORADA_Localidade', e.target.value)}
                              isInvalid={!!errors.FIADOR2_MORADA_Localidade}
                              required
                              id="FIADOR2_MORADA_Localidade"
                              name="FIADOR2_MORADA_Localidade"
                              aria-label="Locality"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_MORADA_Localidade}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Número de Telefone *</Form.Label>
                            <PhoneInput
                              international
                              countryCallingCodeEditable={false}
                              defaultCountry="PT"
                              value={formData.FIADOR2_MobilePhoneNumber}
                              onChange={(value) => handleInputChange('FIADOR2_MobilePhoneNumber', value)}
                              onBlur={() => validateField('FIADOR2_MobilePhoneNumber', formData.FIADOR2_MobilePhoneNumber)}
                              className={`form-control ${errors.FIADOR2_MobilePhoneNumber ? 'is-invalid' : ''}`}
                              name="FIADOR2_MobilePhoneNumber"
                              id="FIADOR2_MobilePhoneNumber"
                              aria-label="Phone number"
                            />
                            {errors.FIADOR2_MobilePhoneNumber && <div className="invalid-feedback d-block">{errors.FIADOR2_MobilePhoneNumber}</div>}
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                              type="email"
                              value={formData.FIADOR2_Email}
                              onChange={(e) => handleInputChange('FIADOR2_Email', e.target.value)}
                              onBlur={(e) => handleFieldBlur('FIADOR2_Email', e.target.value)}
                              isInvalid={!!errors.FIADOR2_Email}
                              required
                              id="FIADOR2_Email"
                              name="FIADOR2_Email"
                              aria-label="Email address"
                            />
                            <Form.Control.Feedback type="invalid">{errors.FIADOR2_Email}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                <div className="text-center mt-4">
                  <Button 
                    variant="primary" 
                    size="lg" 
                    type="submit" 
                    disabled={isSubmitting || isTokenExpired}
                    className="px-5"
                    aria-label="Submit registration form"
                    data-testid="submit-button"
                  >
                    {isSubmitting ? 'A submeter...' : 'Submeter Registo'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RegistrationForm;
